import {IFirebaseTopicModel} from "./firebase-topic-model.interface";
import {IFirebaseTopicRegistration, IFirebaseTopicViewModel} from "./firebase-topic-view-model.interface";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {ITriple8Connection} from "../triple8/triple8-connection.inteface";
import {IServicesConfigurations} from "../../configurations/config-data-sections/services-configurations";
import {FirebaseAnalyticsReporter} from "../firebase-analytics-reporter";


export abstract class FirebaseTopicModel<TRawReceivedMessage, TPublishMessage> implements IFirebaseTopicModel, IFirebaseTopicViewModel<TPublishMessage> {
    constructor(protected readonly services: IServiceFactoryExtended,
                protected readonly getConnection: () => Promise<ITriple8Connection>) {
        this.analytics = new FirebaseAnalyticsReporter(services);
    }

    abstract getTopicName(): string;

    abstract getTopicLabel(): string;

    protected readonly analytics: FirebaseAnalyticsReporter;

    protected get _shouldRemoveConsumedMessages(): boolean {
        return false;
    }

    protected _getSubscriptionParams(): Record<string, any> {
        return {};
    }

    protected get configs(): IServicesConfigurations["PlayerMessages"]["Firebase"] {
        return this.services.configuration.servicesConfigs.PlayerMessages.Firebase;
    }

    private _listeners: ((message: TPublishMessage) => void)[] = [];

    registerListener(callback: (message: TPublishMessage) => void): IFirebaseTopicRegistration {
        this._listeners.push(callback);
        if(this._lastPublishedMessage) {
            this._notifyOneListener(callback, this._lastPublishedMessage);
        }

        return {
            unregister: () => {
                const index = this._listeners.indexOf(callback);
                if(index >= 0) {
                    this._listeners.splice(index, 1);
                }
            }
        };
    }

    protected abstract _mapRawMessageToPublishMessage(rawMessage: TRawReceivedMessage): TPublishMessage;

    private _lastPublishedMessage: TPublishMessage | null = null;
    protected notifyListeners(rawMessage: TRawReceivedMessage): void {
        const messageToPublish = this._mapRawMessageToPublishMessage(rawMessage);
        if(!this._shouldRemoveConsumedMessages){
            this._lastPublishedMessage = messageToPublish;
        }

        for (const listener of this._listeners) {
           this._notifyOneListener(listener, messageToPublish);
        }
    }

    private _notifyOneListener(listener: (message: TPublishMessage) => void, messageToPublish: TPublishMessage): void {
        try {
            listener(messageToPublish);
        }
        catch (error) {
            this.services.logger.error(`Failed to notify firebase listener ${listener}`, error);
        }
    }

    protected async _isEnabled(): Promise<boolean> {
        return true;
    }


    private _unsubscribeCallback: (() => void) | null = null;
    async subscribe(): Promise<void> {
        if(!await this._isEnabled()) {
            return;
        }


        if(this._unsubscribeCallback) {
            this.services.logger.error(`Already subscribed to topic ${this.getTopicName()}`);
            return;
        }

        const connection = await this.getConnection();
        if(this._shouldRemoveConsumedMessages) {
            this._unsubscribeCallback = await connection.subscribeWithAckAsync(
                this.getTopicName(),
                (message: TRawReceivedMessage) =>  this.notifyListeners(message),
                this._getSubscriptionParams());
        } else {
            this._unsubscribeCallback = await connection.subscribeAsync(
                this.getTopicName(),
(message: TRawReceivedMessage) => this.notifyListeners(message),
                this._getSubscriptionParams());
        }

    }

    async unsubscribe(): Promise<void> {
        this._lastPublishedMessage = null;
        if(this._unsubscribeCallback) {
            try {
                this._unsubscribeCallback();
            } finally {
                this._unsubscribeCallback = null;
            }
        }
    }

}